// Impact Tables management page (FRMW-003).
// Tabs across the top for each active impact type (Operational, Financial, etc.)
// with versioned level management per tab. ⋮ menu on each tab for rename/deactivate.
// Tables without an active version are hidden. Level count locked by scoring grid.

import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  impactTablesApi,
  scoringGridApi,
  deriveVersionStatus,
  type ImpactTable,
  type LevelInput,
  type ActivationResponse,
} from '@/lib/api/frameworkConfig'
import { useEntitlements } from '@/lib/hooks/use-entitlements'
import { impactTableSchema, type ImpactTableFormData } from '@/lib/schemas/frameworkConfig'
import { queryKeys } from '@/lib/api/queryKeys'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { useToast } from '@/lib/hooks/use-toast'
import {
  Plus,
  Pencil,
  Play,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Loader2,
  MoreVertical,
} from 'lucide-react'

import {
  ActiveLevelDisplay,
  VersionList,
  LevelEditorDialog,
  EditDraftLoader,
  ActivateConfirmDialog,
  emptyLevels,
  type PageMode,
} from '@/components/features/versioned-levels'
import type { VersionedLevelLabels } from '@/components/features/versioned-levels/types'

export function ImpactTablesPage() {
  const { t } = useTranslation(['frameworkConfig', 'common'])
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const itApi = impactTablesApi(api)
  const sgApi = scoringGridApi(api)

  const [selectedTableId, setSelectedTableId] = useState<string | null>(null)
  const [showAddTable, setShowAddTable] = useState(false)

  // Fetch scoring grid
  const scoringGridQuery = useQuery({
    queryKey: queryKeys.frameworkConfig.scoringGrid(slug),
    queryFn: () => sgApi.get(),
    retry: (count, error) => {
      if (error instanceof ApiRequestError && error.status === 404) return false
      return count < 3
    },
  })

  // Fetch all impact tables
  const tablesQuery = useQuery({
    queryKey: queryKeys.frameworkConfig.impactTables(slug),
    queryFn: () => itApi.list(),
  })

  // Create new table
  const createTableMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) => itApi.create(data),
    onSuccess: (newTable) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.frameworkConfig.impactTables(slug) })
      setSelectedTableId(newTable.id)
      setShowAddTable(false)
      toast({ title: t('impactTables.tableCreated') })
    },
    onError: (err) => {
      toast({
        title: t('common:errors.generic'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  if (scoringGridQuery.isLoading || tablesQuery.isLoading) {
    return <TableSkeleton />
  }

  if (
    scoringGridQuery.error &&
    !(scoringGridQuery.error instanceof ApiRequestError && scoringGridQuery.error.status === 404)
  ) {
    return (
      <ErrorState
        message={t('common:errors.loadFailed', { resource: t('scoringGrid.title') })}
        technicalDetail={
          scoringGridQuery.error instanceof ApiRequestError
            ? scoringGridQuery.error.message
            : scoringGridQuery.error.message
        }
        onRetry={() => scoringGridQuery.refetch()}
      />
    )
  }

  if (!scoringGridQuery.data) {
    return (
      <div className="max-w-3xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold">{t('impactTables.title')}</h1>
          <p className="text-muted-foreground">{t('impactTables.description')}</p>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center gap-3 py-8 text-muted-foreground">
            <AlertTriangle className="h-5 w-5" />
            <span>{t('impactTables.scoringGridRequired')}</span>
          </CardContent>
        </Card>
      </div>
    )
  }

  const levelCount = scoringGridQuery.data.target_impact_level_count
  const allTables = tablesQuery.data ?? []

  // Auto-select first table if current selection doesn't exist
  const activeTableId =
    allTables.find((t) => t.id === selectedTableId)?.id ?? allTables[0]?.id ?? null
  const activeTable = allTables.find((t) => t.id === activeTableId) ?? null
  const canDeactivate = allTables.filter((t) => t.active_version_id !== null).length > 1

  return (
    <div className="max-w-3xl space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">{t('impactTables.title')}</h1>
        <p className="text-muted-foreground">
          {t('impactTables.description')} {t('impactTables.levelCountInfo', { count: levelCount })}
        </p>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b">
        {allTables.map((table) => (
          <TabWithMenu
            key={table.id}
            table={table}
            isSelected={table.id === activeTableId}
            canDeactivate={canDeactivate}
            onSelect={() => {
              setSelectedTableId(table.id)
              setShowAddTable(false)
            }}
          />
        ))}
        <button
          onClick={() => setShowAddTable(true)}
          className="px-3 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          title={t('impactTables.addTable')}
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      {/* Add table form */}
      {showAddTable && (
        <AddTableForm
          isPending={createTableMutation.isPending}
          onSubmit={(data) => createTableMutation.mutate(data)}
          onCancel={() => setShowAddTable(false)}
        />
      )}

      {/* Selected table version management */}
      {activeTable && !showAddTable && (
        <ImpactTableVersionPanel key={activeTable.id} table={activeTable} levelCount={levelCount} />
      )}

      {/* No tables */}
      {allTables.length === 0 && !showAddTable && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {t('impactTables.noTables')}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// --- Tab with ⋮ context menu ---

function TabWithMenu({
  table,
  isSelected,
  canDeactivate,
  onSelect,
}: {
  table: ImpactTable
  isSelected: boolean
  canDeactivate: boolean
  onSelect: () => void
}) {
  const { t } = useTranslation(['frameworkConfig', 'common'])
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const itApi = impactTablesApi(api)

  const [menuOpen, setMenuOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [deactivateOpen, setDeactivateOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen])

  const renameMutation = useMutation({
    mutationFn: (name: string) => itApi.update(table.id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.frameworkConfig.impactTables(slug) })
      setRenameOpen(false)
      toast({ title: t('impactTables.renamed') })
    },
    onError: (err) => {
      toast({
        title: t('common:errors.generic'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: () => itApi.deactivate(table.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.frameworkConfig.impactTables(slug) })
      setDeactivateOpen(false)
      toast({ title: t('impactTables.deactivated') })
    },
    onError: (err) => {
      toast({
        title: t('common:errors.generic'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  return (
    <>
      <div className="relative flex items-center" ref={menuRef}>
        <button
          onClick={onSelect}
          className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            isSelected
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:border-muted-foreground/30 hover:text-foreground'
          }`}
        >
          {table.name}
        </button>
        {isSelected && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              setMenuOpen(!menuOpen)
            }}
            className="-ml-1 p-1 text-muted-foreground transition-colors hover:text-foreground"
          >
            <MoreVertical className="h-3.5 w-3.5" />
          </button>
        )}

        {/* Dropdown menu */}
        {menuOpen && (
          <div className="absolute left-0 top-full z-50 mt-1 min-w-[140px] rounded-md border bg-popover py-1 shadow-md">
            <button
              onClick={() => {
                setMenuOpen(false)
                setRenameOpen(true)
              }}
              className="w-full px-3 py-1.5 text-left text-sm transition-colors hover:bg-accent"
            >
              {t('impactTables.rename')}
            </button>
            <button
              onClick={() => {
                setMenuOpen(false)
                setDeactivateOpen(true)
              }}
              disabled={!canDeactivate}
              className="w-full px-3 py-1.5 text-left text-sm text-destructive transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('impactTables.deactivateTable')}
            </button>
          </div>
        )}
      </div>

      {/* Rename dialog */}
      {renameOpen && (
        <RenameDialog
          currentName={table.name}
          isPending={renameMutation.isPending}
          onSubmit={(name) => renameMutation.mutate(name)}
          onCancel={() => setRenameOpen(false)}
        />
      )}

      {/* Deactivate confirmation */}
      {deactivateOpen && (
        <DeactivateConfirmDialog
          tableName={table.name}
          isPending={deactivateMutation.isPending}
          onConfirm={() => deactivateMutation.mutate()}
          onCancel={() => setDeactivateOpen(false)}
        />
      )}
    </>
  )
}

// --- Rename dialog ---

function RenameDialog({
  currentName,
  isPending,
  onSubmit,
  onCancel,
}: {
  currentName: string
  isPending: boolean
  onSubmit: (name: string) => void
  onCancel: () => void
}) {
  const { t } = useTranslation(['frameworkConfig', 'common'])
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ImpactTableFormData>({
    resolver: zodResolver(impactTableSchema),
    defaultValues: { name: currentName },
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} />
      <Card className="relative z-10 mx-4 w-full max-w-sm">
        <CardHeader>
          <CardTitle>{t('impactTables.rename')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit((data) => onSubmit(data.name))} className="space-y-4">
            <div className="space-y-1">
              <Input autoFocus {...register('name')} />
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
                {t('common:cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t('common:save')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

// --- Deactivate confirmation dialog ---

function DeactivateConfirmDialog({
  tableName,
  isPending,
  onConfirm,
  onCancel,
}: {
  tableName: string
  isPending: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation(['frameworkConfig', 'common'])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} />
      <Card className="relative z-10 mx-4 w-full max-w-sm">
        <CardHeader>
          <CardTitle>{t('impactTables.deactivateTable')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('impactTables.deactivateConfirm', { name: tableName })}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onCancel} disabled={isPending}>
              {t('common:cancel')}
            </Button>
            <Button variant="destructive" onClick={onConfirm} disabled={isPending}>
              {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('impactTables.deactivateTable')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// --- Add table form ---

function AddTableForm({
  isPending,
  onSubmit,
  onCancel,
}: {
  isPending: boolean
  onSubmit: (data: { name: string; description?: string }) => void
  onCancel: () => void
}) {
  const { t } = useTranslation(['frameworkConfig', 'common'])
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ImpactTableFormData>({
    resolver: zodResolver(impactTableSchema),
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('impactTables.addTable')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={handleSubmit((data) =>
            onSubmit({ name: data.name, description: data.description || undefined }),
          )}
          className="space-y-4"
        >
          <div className="space-y-1">
            <Input placeholder={t('impactTables.tableName')} {...register('name')} />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>
          <div className="space-y-1">
            <Input placeholder={t('impactTables.tableDescription')} {...register('description')} />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
              {t('common:cancel')}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('common:create')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

// --- Version management panel for a single impact table ---

function ImpactTableVersionPanel({
  table,
  levelCount,
}: {
  table: ImpactTable
  levelCount: number
}) {
  const { t } = useTranslation(['frameworkConfig', 'common'])
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const itApi = impactTablesApi(api)
  const { data: entitlements } = useEntitlements()
  const hasConfirmationGates = entitlements?.confirmation_gates === true

  const [mode, setMode] = useState<PageMode>({ kind: 'view' })
  const [showVersions, setShowVersions] = useState(false)

  const labels: VersionedLevelLabels = {
    createDraft: t('impactTables.createDraft'),
    editDraft: t('impactTables.editDraft'),
    activate: t('impactTables.activate'),
    activateConfirm: hasConfirmationGates
      ? t('impactTables.activateConfirmWithEmail')
      : t('impactTables.activateConfirm'),
    versionLabel: (n) => t('impactTables.versionLabel', { number: n }),
    statusActive: t('impactTables.statusActive'),
    statusDraft: t('impactTables.statusDraft'),
    statusRetired: t('impactTables.statusRetired'),
    activeLevels: table.name,
    noActiveVersion: t('impactTables.noActiveVersion'),
    noVersions: t('impactTables.noVersions'),
    levelCountInfo: (count) => t('impactTables.levelCountInfo', { count }),
    levelLabel: t('impactTables.levelLabel'),
    levelDescription: t('impactTables.levelDescription'),
    versions: t('impactTables.versions'),
  }

  const versionsQueryKey = queryKeys.frameworkConfig.impactTableVersions(slug, table.id)

  const versionsQuery = useQuery({
    queryKey: versionsQueryKey,
    queryFn: () => itApi.listVersions(table.id),
  })

  const versions = versionsQuery.data ?? []
  const activeVersionSummary = versions.find((v) => v.is_active)
  const draftVersion = versions.find((v) => deriveVersionStatus(v) === 'draft')

  const activeDetailQuery = useQuery({
    queryKey: [...versionsQueryKey, 'active-detail', activeVersionSummary?.id],
    queryFn: () => itApi.getVersion(table.id, activeVersionSummary!.id),
    enabled: !!activeVersionSummary,
  })

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.frameworkConfig.impactTables(slug) })
    queryClient.invalidateQueries({ queryKey: versionsQueryKey })
  }

  const createMutation = useMutation({
    mutationFn: (levels: LevelInput[]) => itApi.createVersion(table.id, levels),
    onSuccess: () => {
      invalidateAll()
      setMode({ kind: 'view' })
      toast({ title: t('impactTables.draftCreated') })
    },
    onError: (err) => {
      toast({
        title: t('common:errors.generic'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  const editMutation = useMutation({
    mutationFn: ({ versionId, levels }: { versionId: string; levels: LevelInput[] }) =>
      itApi.editVersion(table.id, versionId, levels),
    onSuccess: () => {
      invalidateAll()
      setMode({ kind: 'view' })
      toast({ title: t('impactTables.draftUpdated') })
    },
    onError: (err) => {
      toast({
        title: t('common:errors.generic'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  const activateMutation = useMutation({
    mutationFn: (versionId: string): Promise<ActivationResponse> => itApi.activate(table.id, versionId),
    onSuccess: (data) => {
      if (data.status === 'pending_confirmation') {
        setMode({ kind: 'view' })
        toast({
          title: t('impactTables.confirmationSent'),
          description: t('impactTables.checkEmail'),
        })
        return
      }
      invalidateAll()
      setMode({ kind: 'view' })
      toast({ title: t('impactTables.activated') })
    },
    onError: (err) => {
      toast({
        title: t('common:errors.generic'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  if (versionsQuery.isLoading) {
    return <TableSkeleton />
  }

  const activeDetail = activeDetailQuery.data
  const hasActive = !!activeDetail

  return (
    <div className="space-y-4">
      {/* Action buttons */}
      <div className="flex justify-end gap-2">
        {draftVersion ? (
          <>
            <Button
              variant="outline"
              onClick={() =>
                setMode({
                  kind: 'edit',
                  versionId: draftVersion.id,
                  versionNumber: draftVersion.version_number,
                })
              }
            >
              <Pencil className="mr-2 h-4 w-4" /> {labels.editDraft}
            </Button>
            <Button
              onClick={() =>
                setMode({
                  kind: 'activate-confirm',
                  versionId: draftVersion.id,
                  versionNumber: draftVersion.version_number,
                })
              }
            >
              <Play className="mr-2 h-4 w-4" /> {labels.activate}
            </Button>
          </>
        ) : (
          <Button onClick={() => setMode({ kind: 'create' })}>
            <Pencil className="mr-2 h-4 w-4" /> {labels.createDraft}
          </Button>
        )}
      </div>

      {/* Active version display */}
      {hasActive ? (
        <ActiveLevelDisplay
          versionNumber={activeDetail.version_number}
          levels={activeDetail.levels}
          labels={labels}
        />
      ) : activeDetailQuery.isLoading ? (
        <TableSkeleton />
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {labels.noActiveVersion}
          </CardContent>
        </Card>
      )}

      {/* Version history toggle */}
      <Button variant="ghost" className="text-sm" onClick={() => setShowVersions(!showVersions)}>
        {showVersions ? (
          <ChevronUp className="mr-2 h-4 w-4" />
        ) : (
          <ChevronDown className="mr-2 h-4 w-4" />
        )}
        {labels.versions}
      </Button>

      {showVersions && (
        <VersionList
          versions={versions}
          isLoading={versionsQuery.isLoading}
          labels={labels}
          onEditDraft={(v) =>
            setMode({ kind: 'edit', versionId: v.id, versionNumber: v.version_number })
          }
          onActivate={(v) =>
            setMode({ kind: 'activate-confirm', versionId: v.id, versionNumber: v.version_number })
          }
        />
      )}

      {mode.kind === 'create' && (
        <LevelEditorDialog
          mode="create"
          levelCount={levelCount}
          initialLevels={
            activeDetail?.levels.map((l) => ({
              level_number: l.level_number,
              label: l.label,
              description: l.description ?? '',
            })) ?? emptyLevels(levelCount)
          }
          isPending={createMutation.isPending}
          labels={labels}
          saveLabel={t('common:save')}
          createLabel={t('common:create')}
          savingLabel={t('common:saving')}
          cancelLabel={t('common:cancel')}
          onSubmit={(levels) => createMutation.mutate(levels)}
          onCancel={() => setMode({ kind: 'view' })}
        />
      )}

      {mode.kind === 'edit' && (
        <EditDraftLoader
          versionId={mode.versionId}
          versionNumber={mode.versionNumber}
          levelCount={levelCount}
          isPending={editMutation.isPending}
          labels={labels}
          saveLabel={t('common:save')}
          cancelLabel={t('common:cancel')}
          savingLabel={t('common:saving')}
          queryKey={[...versionsQueryKey, mode.versionId]}
          fetchVersion={(vid) => itApi.getVersion(table.id, vid)}
          onSubmit={(levels) => editMutation.mutate({ versionId: mode.versionId, levels })}
          onCancel={() => setMode({ kind: 'view' })}
        />
      )}

      {mode.kind === 'activate-confirm' && (
        <ActivateConfirmDialog
          versionNumber={mode.versionNumber}
          isPending={activateMutation.isPending}
          labels={labels}
          cancelLabel={t('common:cancel')}
          onConfirm={() => activateMutation.mutate(mode.versionId)}
          onCancel={() => setMode({ kind: 'view' })}
        />
      )}
    </div>
  )
}
